import type { SeriesClickEventObject } from "highcharts";
import { Set } from "immutable";
import type { RefObject } from "react";

/**
 * The selection logic shared by the charts whose X axis *is* the portfolios — the column chart and the
 * stacked column. Both draw one bar (or one stack) per portfolio, so both select the same way: click to
 * toggle one, shift-click to extend to a range, drag a band to take everything it spans.
 *
 * The two differ only in how a selected bar is *drawn* — the column chart recolours it, the stacked one
 * cannot (colour is its category encoding) and greys the rest out instead — which is a render concern and
 * stays in the components.
 */

export type SelectableChartItem = { id: string };

/**
 * The portfolios spanned by an inclusive range of category indices, for band-drag selection.
 *
 * Indices come from the axis and can overshoot it in either direction (a band dragged past the plot, or
 * right to left), so they are ordered and clamped here rather than at every call site.
 */
export function itemIdsInIndexRange(
	items: ReadonlyArray<SelectableChartItem>,
	fromIndex: number,
	toIndex: number,
): Array<string> {
	const lo = Math.max(0, Math.min(fromIndex, toIndex));
	const hi = Math.min(items.length - 1, Math.max(fromIndex, toIndex));
	const spanned: Array<string> = [];
	for (let i = lo; i <= hi; i++) {
		spanned.push(items[i].id);
	}
	return spanned;
}

/**
 * Toggles the covered portfolios against `initialSelection`, or replaces it — the two ways a band drag can
 * land, `toggle` being the shift-held one.
 */
export function applyBandSelection(
	initialSelection: Set<string>,
	spannedIds: Set<string>,
	mode: "toggle" | "new",
): Set<string> {
	if (mode === "new") {
		return spannedIds;
	}
	return initialSelection.subtract(spannedIds).union(spannedIds.subtract(initialSelection));
}

/**
 * A Highcharts point-click handler that toggles the clicked portfolio, or — with shift — selects the range
 * between it and the last anchor. `shiftSelectionIdRef` is that anchor, and the band drag writes it too so
 * a shift-click can extend a dragged selection.
 */
export function handleSelectionClick(
	onChange: (newSelection: Set<string>) => void,
	{
		data,
		selectedIds,
		shiftSelectionIdRef,
	}: {
		data: ReadonlyArray<SelectableChartItem>;
		shiftSelectionIdRef: RefObject<string | null>;
		selectedIds: Set<string>;
	},
) {
	return function core(e: SeriesClickEventObject) {
		let newSelection: Set<string>;
		const dataIndex = e.point.index;
		const id = data[dataIndex].id;
		if ((e as unknown as { shiftKey?: boolean }).shiftKey) {
			const refIndex = data.findIndex((point) => point.id === shiftSelectionIdRef.current);
			if (refIndex === -1) {
				newSelection = selectedIds.union([id]);
			} else {
				newSelection = Set(
					data.map((point) => point.id).slice(Math.min(refIndex, dataIndex), Math.max(refIndex, dataIndex) + 1),
				);
			}
		} else if (selectedIds.includes(id)) {
			newSelection = selectedIds.remove(id);
		} else {
			newSelection = selectedIds.add(id);
			shiftSelectionIdRef.current = id;
		}

		onChange(newSelection);
	};
}
